"use client";

import { motion } from "framer-motion";
import { PortableText } from "@portabletext/react";
import { PortableTextBlock } from "sanity";

import TextScrollFadeIn from "@/components/global/partials/TextScrollFadeIn";
import { GroqImage } from "@/helpers/Image";
import { GROQImage } from "@/sanity/lib/definitions";
import { richTextComponent } from "@/sanity/sanityPortableText";

const ease = [0.25, 0.1, 0.25, 1] as const;

export interface WhoWeAreSectionData {
  whoIcon?: GROQImage;
  whoTitle?: PortableTextBlock[];
  whoText?: PortableTextBlock[];
}

export default function WhoWeAreSection({ data }: { data?: WhoWeAreSectionData }) {
  if (!data?.whoTitle && !data?.whoText) return null;

  return (
    <section className="w-full bg-white px-[2rem] lg:px-[6rem] max-lg:pt-[4rem] max-lg:pb-[8rem] lg:pt-[10rem] lg:pb-[12.5rem]">
      <div className="mx-auto flex w-[min(169.5rem,100%)] flex-col items-center">
        {data.whoIcon && (
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.8, ease }}
            className="max-lg:h-[10.25rem] max-lg:w-[9.8125rem] lg:h-[12rem] lg:w-[11.5rem]">
            <GroqImage
              src={data.whoIcon}
              alt=""
              quality={100}
              className="!h-full !w-full !object-contain"
            />
          </motion.div>
        )}

        {data.whoTitle && (
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.8, ease, delay: 0.1 }}
            className="richtext w-full max-lg:mt-[2.5rem] lg:mt-[4rem] prose-headings:mb-0 max-lg:prose-headings:text-[4.25rem] max-lg:prose-headings:leading-[1.15] max-lg:prose-headings:tracking-[-0.2125rem] lg:prose-headings:text-[6.5rem] lg:prose-headings:leading-[1.12] lg:prose-headings:tracking-[-0.195rem] [&_h2_span]:leading-[0]">
            <PortableText
              value={data.whoTitle}
              components={richTextComponent}
            />
          </motion.div>
        )}

        {data.whoText && (
          <TextScrollFadeIn
            portableText={data.whoText}
            className="richtext mt-[2.5rem] w-full max-w-[123.5rem] prose-p:mb-0 prose-p:leading-[1.31] prose-p:font-normal prose-p:text-black max-lg:px-[1.25rem] max-lg:prose-p:text-[2.75rem] max-lg:prose-p:tracking-[-0.055rem] lg:mt-[4rem] lg:px-0 lg:prose-p:text-[4rem] lg:prose-p:tracking-[-0.08rem]"
          />
        )}
      </div>
    </section>
  );
}
